import { useState, useCallback } from 'react';
import TripModeToggle from './TripModeToggle';
import SearchOverlay from './SearchOverlay';
import StopsList from './StopsList';
import GoogleMapsStatus from '../shared/GoogleMapsStatus';
import LoadingState from '../shared/LoadingState';
import MobileItineraryView from './MobileItineraryView';
import useTripStore from '../../context/TripStore';
import { useItineraryGenerator } from '../../hooks/useItineraryGenerator';

/**
 * Mobile layout with full-screen menu
 */
function MobileLayout() {
  // All hooks must be called before any conditional returns
  const currentStep = useTripStore((state) => state.ui?.currentStep ?? 'list');
  const isGenerating = useTripStore((state) => state.ui?.isGenerating ?? false);
  const places = useTripStore((state) => state.places ?? []);
  const savedTrips = useTripStore((state) => state.savedTrips ?? []);
  const startLocation = useTripStore((state) => state.trip?.startLocation);

  // Itinerary generator hook
  const { generate, error: generateError } = useItineraryGenerator();

  // Local UI state for mobile
  const [tripMode, setTripMode] = useState('new'); // 'new' | 'previous'
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(null); // 'start' | 'stops'
  const [error, setError] = useState(null);

  // Open search overlay for start location
  const handleOpenStartSearch = useCallback(() => {
    setSearchMode('start');
    setIsSearchOpen(true);
  }, []);

  // Open search overlay for stops
  const handleOpenStopsSearch = useCallback(() => {
    setSearchMode('stops');
    setIsSearchOpen(true);
  }, []);

  // Handle selection from search overlay
  const handleSearchSelect = useCallback((placeDetails) => {
    if (searchMode === 'start') {
      useTripStore.getState().setTrip({ startLocation: placeDetails });
    } else if (searchMode === 'stops') {
      useTripStore.getState().addPlace({
        placeId: placeDetails.placeId,
        name: placeDetails.name,
        coordinates: placeDetails.coordinates,
        address: placeDetails.address,
        rating: placeDetails.rating,
        types: placeDetails.types,
        photo: placeDetails.photo,
      });
    }
  }, [searchMode]);

  // Handle generate button
  const handleGenerate = useCallback(async () => {
    setError(null);
    try {
      await generate();
      useTripStore.getState().setCurrentStep('itinerary');
    } catch (err) {
      setError(err.message);
      console.error('Failed to generate itinerary:', err);
    }
  }, [generate]);

  // Handle loading saved trip
  const handleLoadTrip = useCallback((tripId) => {
    useTripStore.getState().loadSavedTrip(tripId);
  }, []);

  // Handle deleting saved trip
  const handleDeleteTrip = useCallback((tripId) => {
    useTripStore.getState().deleteSavedTrip(tripId);
  }, []);

  // Check if ready to generate
  const canGenerate = startLocation && places.length > 0;

  // Show itinerary view when on itinerary step
  if (currentStep === 'itinerary') {
    return <MobileItineraryView />;
  }

  // Show loading state when generating
  if (isGenerating) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
        <LoadingState message="Generating your itinerary..." />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white flex flex-col overflow-hidden">
      {/* Full-screen search overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        mode={searchMode}
        onSelect={handleSearchSelect}
      />

      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-gray-100">
        <h1 className="text-lg font-bold text-gray-900">Plan Your Trip</h1>
      </div>

      {/* Main content - scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3">
        {/* Trip mode toggle */}
        <TripModeToggle mode={tripMode} onModeChange={setTripMode} />

        {tripMode === 'new' ? (
          <div className="flex flex-col gap-3 mt-3">
            {/* Starting location */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Starting point
              </label>
              <button
                type="button"
                onClick={handleOpenStartSearch}
                className="w-full px-3 py-3 bg-gray-100 rounded-lg text-left flex items-center gap-2 hover:bg-gray-200 active:bg-gray-200 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r="4" />
                  </svg>
                </div>
                <span className={`flex-1 truncate text-sm ${startLocation ? 'text-gray-900' : 'text-gray-500'}`}>
                  {startLocation?.name || 'Search starting location...'}
                </span>
              </button>
            </div>

            {/* Add stops */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Add stops
              </label>
              <button
                type="button"
                onClick={handleOpenStopsSearch}
                className="w-full px-3 py-3 bg-gray-100 rounded-lg text-left flex items-center gap-2 hover:bg-gray-200 active:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="flex-1 text-sm text-gray-500">Search places to visit...</span>
              </button>
            </div>

            {/* Added stops list */}
            {places.length > 0 && (
              <div className="mt-1">
                <StopsList />
              </div>
            )}

            {/* Error message */}
            {(error || generateError) && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error || generateError}</p>
              </div>
            )}

            {/* Hint when not ready */}
            {!canGenerate && !error && (
              <div className="mt-3 text-center text-xs text-gray-500">
                {!startLocation && 'Set a starting location to get started'}
                {startLocation && places.length === 0 && 'Add at least one stop to visit'}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3">
            {/* Previous trips list */}
            {savedTrips.length > 0 ? (
              <div className="space-y-2">
                {savedTrips.map((trip) => (
                  <div
                    key={trip.id}
                    className="bg-gray-50 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {trip.trip?.city?.name || 'Trip'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {trip.places?.length || 0} stops · {new Date(trip.savedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleLoadTrip(trip.id)}
                          className="px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => handleDeleteTrip(trip.id)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <svg
                  className="w-10 h-10 mx-auto mb-2 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
                <p className="text-sm">No previous trips yet</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Your saved itineraries will appear here
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed bottom button */}
      {canGenerate && tripMode === 'new' && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-white">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed active:bg-blue-700 transition-colors text-sm"
          >
            Generate Itinerary
          </button>
        </div>
      )}

      {/* Google Maps status */}
      <GoogleMapsStatus />
    </div>
  );
}

export default MobileLayout;
